// ***START: ComponentAccordion***
/*
- use `data-trigger` & `data-target` attribute
- use `data-close-sibling` attribute, if you wat close all sibling when - open the accordion
- use `c-accordion__target` & `is-active` class name

- use case:
<c-accordion data-close-sibling="true">
	{% for block in blocks %}
		<button data-trigger="{{ block.id }}">
			{{ block.settings.title }}
		</button>
		<div class="c-accordion__target" data-target="{{ block.id }}" aria-hidden="true">
			<div class="c-accordion__content">{{ block.settings.content }}</div>
		</div>
	{% endfor %}
</c-accordion>
*/
class ComponentAccordion extends HTMLElement {
	constructor() {
		super();
		this.triggers = this.querySelectorAll("[data-trigger]");
		this.targets = this.querySelectorAll("[data-target]");
		this.closeSibling = this.dataset.closeSibling === "true" || false;
		this.toggleAccordion = this.toggleAccordion.bind(this);
	}

	connectedCallback() {
		this.triggers.forEach((trigger) => {
			trigger.addEventListener("click", this.toggleAccordion);
		});
	}

	disconnectedCallback() {
		this.triggers.forEach((trigger) => {
			trigger.removeEventListener("click", this.toggleAccordion);
		});
	}

	toggleAccordion(e) {
		const triggerEl = e.target.closest("[data-trigger]");
		const currentTargetId = triggerEl.dataset.trigger;
		const targetEls = this.querySelectorAll(
			`[data-target="${currentTargetId}"]`
		);

		if (!triggerEl || !targetEls) return;

		// toggle current accordion
		if (triggerEl.classList.contains("is-active")) {
			triggerEl.classList.remove("is-active");
			targetEls.forEach((el) => {
				el.classList.remove("is-active");
				el.setAttribute("aria-hidden", true);
			});
			return;
		}

		if (this.closeSibling) {
			this.triggers.forEach((el) => {
				el.classList.remove("is-active");
			});
			this.targets.forEach((el) => {
				el.classList.remove("is-active");
				el.setAttribute("aria-hidden", true);
			});
		}

		triggerEl.classList.add("is-active");
		targetEls.forEach((el) => {
			el.classList.add("is-active");
			el.setAttribute("aria-hidden", false);
		});
	}
}
customElements.define("c-accordion", ComponentAccordion);
// ***END: ComponentAccordion***

// place global, component, and page level functions here
const playTargetVideo = (
	video,
	externalVideoType,
	player,
	soundOn = false,
	videoHasMuteBtn = false
) => {
	const playVideo = (video) => {
		video.muted = !(videoHasMuteBtn && soundOn);
		video.play();
	};

	const playExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.setMuted(!(videoHasMuteBtn && soundOn));
			player.play().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error playing video:", error);
				}
			});
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.playVideo == "function" &&
				typeof player.mute === "function"
			) {
				videoHasMuteBtn && soundOn ? player.unMute() : player.mute();
				player.playVideo();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		playVideo(video);
	} else if (player) {
		playExternalVideo(player, externalVideoType);
	}
};

const pauseTargetVideo = (video, externalVideoType, player) => {
	const pauseVideo = (video) => {
		video.pause();
		video.muted = true;
	};

	const pauseExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.pause().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error pausing video:", error);
				}
			});
			player.setMuted(true);
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.pauseVideo === "function" &&
				typeof player.mute === "function"
			) {
				player.pauseVideo();
				player.mute();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		pauseVideo(video);
	} else if (player) {
		pauseExternalVideo(player, externalVideoType);
	}
};
const initVideo = () => {
	const handleVideoPlayback = (item, play, targetPlayer) => {
		const videoElement = item.querySelector("video");
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const videoHasMuteBtn = item.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);
		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		if (!videoElement && !targetPlayer) {
			return;
		}

		if (play) {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		}
	};

	const handleVideoVisibility = (entry, item) => {
		const isVisible = entry.isIntersecting;
		const videoButtons = item.querySelectorAll(
			".js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause"
		);
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		handleVideoPlayback(item, isVisible, targetPlayer);

		// Reset video buttons and toggle based on visibility
		videoButtons.forEach((button) => button.classList.remove("is-active"));
		if (isVisible) {
			item.querySelector(".js-video-unmute")?.classList.add("is-active");
			item.querySelector(".js-video-pause")?.classList.add("is-active");
		}
	};

	// always play/pause video if section is in view
	document.querySelectorAll(".c-video").forEach((item) => {
		const observer = new IntersectionObserver(
			([entry]) => handleVideoVisibility(entry, item),
			{ root: null, threshold: [0.4] }
		);
		observer.observe(item);
	});

	const handleVideoControl = (e, action) => {
		const target = e.target.closest(`.js-video-${action}`);
		const videoContainer = target.closest(".c-video");
		const videoElement = videoContainer.querySelector("video");
		const externalVideo = videoContainer.querySelector(".js-external-video");
		const videoHasMuteBtn = videoContainer.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);
		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		if (action === "play") {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else if (action === "pause") {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		} else if (action === "mute" || action === "unmute") {
			if (externalVideoType === "youtube") {
				if (typeof targetPlayer.unMute == "function") {
					targetPlayer.isMuted() ? targetPlayer.unMute() : targetPlayer.mute();
				} else {
					console.warn(
						"YouTube player not fully initialized or methods unavailable."
					);
				}
			} else if (externalVideoType === "vimeo") {
				targetPlayer.getMuted().then((muted) => targetPlayer.setMuted(!muted));
			} else if (videoElement) {
				videoElement.muted = action === "mute" ? true : false;
			}
		}

		// Toggle play/pause or mute/unmute button visibility
		if (action === "play" || action === "pause") {
			target.classList.remove("is-active");
			videoContainer
				.querySelector(`.js-video-${action === "play" ? "pause" : "play"}`)
				?.classList.add("is-active");
		} else if (action === "mute" || action === "unmute") {
			videoContainer
				.querySelector(".js-video-mute")
				?.classList.toggle("is-active", action === "unmute");
			videoContainer
				.querySelector(".js-video-unmute")
				?.classList.toggle("is-active", action === "mute");
		}
	};

	on("body", "click", ".js-video-mute", (e) => handleVideoControl(e, "mute"));
	on("body", "click", ".js-video-unmute", (e) =>
		handleVideoControl(e, "unmute")
	);
	on("body", "click", ".js-video-play", (e) => handleVideoControl(e, "play"));
	on("body", "click", ".js-video-pause", (e) => handleVideoControl(e, "pause"));

	on("body", "click", ".js-product-hero-slider-thumbnail", (e) => {
		// Note: The setFirstActiveImage function triggers this click event
		const sliderSelector = `.js-product-hero-slider${
			window.innerWidth <= 1024 ? "" : ".is-scroll"
		}`;
		const slider = e.target.closest(sliderSelector);

		// Exit if no slider or no video in the slider
		if (!slider || !slider.querySelector(".c-video")) return;

		// Pause all videos in the slider
		slider.querySelectorAll(".c-video").forEach((videoContainer) => {
			const videoElement = videoContainer.querySelector("video");
			const externalVideo = videoContainer.querySelector(".js-external-video");
			const externalVideoType = externalVideo?.dataset.videoHost;
			const externalVideoId = externalVideo?.dataset.videoId;
			const targetPlayer = externalVideoId
				? allPlayersObj[externalVideoId]
				: null;

			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);

			// Reset video buttons
			videoContainer
				.querySelectorAll(
					".js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause"
				)
				.forEach((button) => button.classList.remove("is-active"));
		});

		// Play the target video after a short delay
		setTimeout(() => {
			const activeVideoContainer = slider.querySelector(
				".js-slider-block.is-active .c-video"
			);
			if (!activeVideoContainer) return;

			const videoElement = activeVideoContainer.querySelector("video");
			const externalVideo =
				activeVideoContainer.querySelector(".js-external-video");
			const externalVideoType = externalVideo?.dataset.videoHost;
			const externalVideoId = externalVideo?.dataset.videoId;
			const targetPlayer = externalVideoId
				? allPlayersObj[externalVideoId]
				: null;

			playTargetVideo(videoElement, externalVideoType, targetPlayer);

			activeVideoContainer
				.querySelectorAll(".js-video-unmute, .js-video-pause")
				.forEach((button) => button.classList.add("is-active"));
		}, 100);
	});
};

const initHeader = () => {
	const mobileMenu = document.querySelector(".js-mobile-menu");
	const overlay = document.querySelector(".js-g-overlay");

	const toggleMobileMenu = (isActive = false) => {
		root.classList.toggle("is-mobile-menu-active", isActive);
		mobileMenu.setAttribute("aria-hidden", !isActive);
	};

	const toggleOverlay = (isActive = false) => {
		overlay.setAttribute("tabindex", isActive ? 0 : -1);
	};

	const closeAllMenu = () => {
		scrollEnable();
		root.classList.remove("is-mobile-menu-active", "is-search-active");
	};

	let lastScrollY = 0;
	let isHeaderShrink = false;
	let ticking = false;

	const updateHeader = () => {
		const scrollY = window.scrollY;
		const shouldShrink = scrollY > 0;

		// Only update if the shrink state has changed
		if (shouldShrink !== isHeaderShrink) {
			isHeaderShrink = shouldShrink;

			if (shouldShrink) {
				root.classList.add("is-header-shrink");
			} else {
				root.classList.remove("is-header-shrink");
			}

			// Update header height CSS variable
			setTimeout(() => {
				root.style.setProperty(
					"--s-header",
					`${document.querySelector(".g-header").offsetHeight}px`
				);
			}, 250);
		}

		ticking = false;
	};

	window.addEventListener("scroll", () => {
		lastScrollY = window.scrollY;

		// Throttle using requestAnimationFrame
		if (!ticking) {
			requestAnimationFrame(updateHeader);
			ticking = true;
		}
	});

	// Listen for custom event from quiz
	document.addEventListener("quiz:close-all-menus", () => {
		closeAllMenu();
	});

	on("body", "click", ".js-g-overlay", () => {
		closeAllMenu();
	});

	on("body", "keydown", "body", (e) => {
		if (e.key == "Escape") {
			closeAllMenu();
		}
	});

	on("body", "click", ".js-mobile-menu-toggle", () => {
		const isActive = !root.classList.contains("is-mobile-menu-active");

		toggleMobileMenu(isActive);
		toggleOverlay(isActive);

		if (isActive) {
			scrollDisable();
		} else {
			scrollEnable();
		}
	});
};

const initQuiz = () => {
	const overlay = document.querySelector(".js-g-overlay");
	const quizModal = document.querySelector(".js-quiz-modal");
	const quizIframe = document.querySelector(".jebbit-iframe");
	const root = document.documentElement || document.body;

	if (!overlay || !quizModal || !quizIframe) {
		console.warn("Quiz initialization failed: required elements not found");
		return;
	}

	let isQuizActive = false;
	let lastFocusedElement = null;
	const originalIframeSrc = quizIframe.src;

	const initQuizIframe = () => {
		// Reset iframe src to force reload
		quizIframe.src = "";
		setTimeout(() => {
			quizIframe.src = originalIframeSrc;
		}, 100);
	};

	const toggleOverlay = (isActive = false) => {
		overlay.setAttribute("tabindex", isActive ? "0" : "-1");
		overlay.setAttribute("aria-hidden", !isActive);
	};

	const toggleQuiz = (isActive = false) => {
		isQuizActive = isActive;
		root.classList.toggle("is-quiz-active", isActive);
		quizModal.setAttribute("aria-hidden", !isActive);

		if (isActive) {
			if (!quizModal.hasAttribute("tabindex")) {
				quizModal.setAttribute("tabindex", "-1");
			}
			quizModal.focus();
		}
	};

	const closeQuiz = () => {
		if (!isQuizActive) return;

		scrollEnable();
		toggleQuiz(false);
		toggleOverlay(false);
		// Close all menus in header
		document.dispatchEvent(new CustomEvent("quiz:close-all-menus"));
		// Reinitialize quiz after closing
		initQuizIframe();

		if (lastFocusedElement) {
			lastFocusedElement.focus();
			lastFocusedElement = null;
		}
	};

	const openQuiz = () => {
		if (isQuizActive) return;

		scrollDisable();
		toggleQuiz(true);
		toggleOverlay(true);
	};

	const handleOverlayClick = (e) => {
		if (e.target === overlay) {
			closeQuiz();
		}
	};

	const handleKeydown = (e) => {
		if (!isQuizActive) return;

		if (e.key === "Escape") {
			e.preventDefault();
			closeQuiz();
		}
	};

	const handleToggleClick = (e) => {
		lastFocusedElement = e.target;

		if (isQuizActive) {
			closeQuiz();
		} else {
			openQuiz();
		}
	};

	on("body", "click", ".js-g-overlay", handleOverlayClick);
	on("body", "keydown", "body", handleKeydown);
	on("body", "click", ".js-quiz-toggle", handleToggleClick);
	on("body", "click", ".js-quiz-exit", closeQuiz);

	return () => {
		off("body", "click", ".js-g-overlay", handleOverlayClick);
		off("body", "keydown", "body", handleKeydown);
		off("body", "click", ".js-quiz-toggle", handleToggleClick);
		off("body", "click", ".js-quiz-exit", closeQuiz);

		if (isQuizActive) {
			scrollEnable();
			root.classList.remove("is-quiz-active");
		}
	};
};

// ANIMATION
const initGSAP = () => {
	gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin);
	ScrollTrigger.config({ ignoreMobileResize: true });
};

const initHeadlineAnimation = () => {
	const headlineElements = document.querySelectorAll(".js-headline-element");
	if (headlineElements.length === 0) return;

	// If user prefers reduced motion, show final state immediately without animation
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		headlineElements.forEach((headlineElement) => {
			const chars = headlineElement.querySelectorAll(".headline__word__char");
			const imgs = document.querySelectorAll(".js-headline-img");

			gsap.set(chars, { opacity: 1, scale: 1, color: "var(--cr-blue)" });
			gsap.set(imgs, { opacity: 1, scale: 1, yPercent: 0 });
		});
		return;
	}

	let colorSequenceData = document.querySelector(".js-headline-color-sequence")
		?.dataset.colorSequence;

	// If it's null, undefined, or empty string, use default sequence
	if (!colorSequenceData) {
		const defaultColorVars =
			"#ed262f, #ffb400, #159f6f, #953bd9 , #ff5500, #139d9c, #e2669d";

		colorSequenceData = defaultColorVars;
	}

	const colors =
		colorSequenceData.split(", ").filter((color) => color.trim()) ?? [];

	const shuffleArray = (array) => {
		const shuffled = [...array];
		for (let i = shuffled.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
		}
		return shuffled;
	};

	// Build the character DOM
	headlineElements.forEach((headlineElement) => {
		const text = headlineElement.textContent.trim();
		headlineElement.innerHTML = "";

		const fragment = document.createDocumentFragment();
		let wordWrapper = null;

		for (let char of text) {
			if (char === " " || char === "\n") {
				wordWrapper = null;

				const space = document.createElement("div");
				space.className = "headline__space";
				space.innerHTML = char === " " ? "&nbsp;" : "<br>";

				fragment.appendChild(space);
			} else {
				if (!wordWrapper) {
					wordWrapper = document.createElement("div");
					wordWrapper.className = "headline__word";
					fragment.appendChild(wordWrapper);
				}

				const charElement = document.createElement("div");
				charElement.className = "headline__word__char";
				charElement.textContent = char;

				wordWrapper.appendChild(charElement);
			}
		}

		headlineElement.appendChild(fragment);
	});

	// Animate headline elements in sequence
	const runAnimation = async () => {
		const chars = document.querySelectorAll(".headline__word__char");
		const imgs = document.querySelectorAll(".js-headline-img");

		// Determine speed factor based on character count
		const speedFactor = chars.length < 15 ? 2 : 1;

		const mainTimeline = gsap.timeline({ delay: 0.1 * speedFactor });

		// Initial state
		mainTimeline
			.set(chars, {
				opacity: 0,
				scale: 0.8,
			})
			.set(imgs, { opacity: 0, scale: 0.8, yPercent: 100 });

		chars.forEach((char, index) => {
			const charTimeline = gsap.timeline();
			const randomColors = [...shuffleArray(colors), "var(--cr-blue)"];
			const midIndex = Math.floor(randomColors.length / 2);

			// Pop-in
			charTimeline.to(char, {
				opacity: 1,
				scale: 1.15,
				duration: 0.03 * speedFactor,
				ease: "none",
			});

			// Color flicker with middle scale down
			randomColors.forEach((color, colorIndex) => {
				const label = colorIndex === midIndex ? "scaleDown" : undefined;
				charTimeline.to(
					char,
					{
						color: color,
						duration: 0,
						delay: colorIndex * 0.01 * speedFactor,
						ease: "none",
					},
					label
				);
			});

			// Final state
			charTimeline.to(
				char,
				{
					scale: 1,
					duration: 0.26 * speedFactor,
					ease: "elastic.out(1,0.4)",
				},
				"scaleDown"
			);

			// Characters show up one by one
			mainTimeline.add(charTimeline, index * 0.03 * speedFactor);
		});

		imgs.forEach((img, index) => {
			const imgTimeline = gsap.timeline();

			// Jump in
			imgTimeline
				.to(img, {
					opacity: 1,
					scale: 1.15,
					duration: 0.03 * speedFactor,
					delay: 1.7 * speedFactor,
					ease: "elastic.out(1,0.3)",
				})
				.to(img, {
					scale: 1,
					yPercent: 0,
					duration: 0.8 * speedFactor,
					ease: "elastic.out(1,0.3)",
				});

			mainTimeline.add(imgTimeline, index * 0.05 * speedFactor);
		});

		return mainTimeline;
	};

	runAnimation();
};

const initButtonAnimation = () => {
	document.querySelectorAll(".btn, .btn-rounded").forEach((btn) => {
		let tl = gsap.timeline({ paused: true });

		tl.to(btn, {
			keyframes: [
				{ scale: 1.2, opacity: 0.9, duration: 0.08 },
				{ scale: 0.6, opacity: 0.9, duration: 0.08 },
				{ scale: 1, opacity: 1, duration: 0.08 },
			],
			ease: "power1.inOut",
			transformOrigin: "center center",
		});

		btn.addEventListener("mouseenter", () => tl.restart());
	});
};

const initSectionAnimation = () => {
	document
		.querySelectorAll("[data-animate-section], section:not(:nth-of-type(1))")
		.forEach((el) => {
			gsap.from(el, {
				y: 50,
				opacity: 0,
				duration: 0.8,
				ease: "power2.out",
				scrollTrigger: {
					trigger: el,
					start: "top 80%",
					toggleActions: "play none none none",
				},
			});
		});
};

const createSVGMorphing = ({
	selector,
	targetSelector,
	blobSelector,
	defaultWidth,
	defaultHeight,
	defaultRadius,
	onMorphToBlob,
	onMorphToRect,
}) => {
	const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
	const blobPathDef = document.querySelector(blobSelector)?.getAttribute("d");

	if (!blobPathDef) {
		console.warn(`Blob path definition not found: ${blobSelector}`);
		return;
	}

	const createRoundedRectPath = (
		width = defaultWidth,
		height = defaultHeight,
		radius = defaultRadius
	) => {
		return `M${radius},0 L${
			width - radius
		},0 Q${width},0 ${width},${radius} L${width},${
			height - radius
		} Q${width},${height} ${
			width - radius
		},${height} L${radius},${height} Q0,${height} 0,${
			height - radius
		} L0,${radius} Q0,0 ${radius},0 Z`;
	};

	const createSVGStructure = (element, index) => {
		const rectId = `morph-rect-${selector.replace(
			/[^a-zA-Z0-9]/g,
			""
		)}-${index}`;
		const blobId = `morph-blob-${selector.replace(
			/[^a-zA-Z0-9]/g,
			""
		)}-${index}`;

		const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
		const clipPath = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"clipPath"
		);
		const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

		// Setup SVG
		Object.assign(svg.style, {
			width: "0",
			height: "0",
			position: "absolute",
			pointerEvents: "none",
		});
		svg.setAttribute("aria-hidden", "true");

		// Setup clipPath and path
		clipPath.id = blobId;
		path.id = rectId;
		path.setAttribute("d", createRoundedRectPath());

		// Assemble structure
		clipPath.appendChild(path);
		defs.appendChild(clipPath);
		svg.appendChild(defs);
		element.appendChild(svg);

		return { svg, clipPath, path, rectId, blobId };
	};

	const setupClipPath = (element, { svg, clipPath, path }, targetElement) => {
		const containerRect = targetElement.getBoundingClientRect();

		if (containerRect.width === 0 || containerRect.height === 0) {
			setTimeout(
				() => setupClipPath(element, { svg, clipPath, path }, targetElement),
				10
			);
			return;
		}

		if (isFirefox) {
			clipPath.setAttribute("clipPathUnits", "userSpaceOnUse");
			svg.setAttribute("width", containerRect.width);
			svg.setAttribute("height", containerRect.height);
			svg.setAttribute(
				"viewBox",
				`0 0 ${containerRect.width} ${containerRect.height}`
			);

			const scaleX = containerRect.width / defaultWidth;
			const scaleY = containerRect.height / defaultHeight;

			if (isFinite(scaleX) && isFinite(scaleY)) {
				path.setAttribute("transform", `scale(${scaleX}, ${scaleY})`);
			}
		} else {
			clipPath.setAttribute("clipPathUnits", "objectBoundingBox");

			const scaleX = 1 / defaultWidth;
			const scaleY = 1 / defaultHeight;

			if (isFinite(scaleX) && isFinite(scaleY)) {
				path.setAttribute("transform", `scale(${scaleX}, ${scaleY})`);
			}
		}
	};

	const setupResizeObserver = (element, svgElements, targetElement) => {
		if (!isFirefox) return;

		if (window.ResizeObserver) {
			const resizeObserver = new ResizeObserver(() => {
				setupClipPath(element, svgElements, targetElement);
			});
			resizeObserver.observe(targetElement);
		} else {
			const resizeHandler = () =>
				setupClipPath(element, svgElements, targetElement);
			window.addEventListener("resize", resizeHandler);
		}
	};

	const createMorphingHandlers = (element, rectId) => {
		const morphToBlob = () => {
			if (onMorphToBlob) onMorphToBlob(element);

			gsap.to(`#${rectId}`, {
				duration: 0.4,
				morphSVG: blobPathDef,
				ease: "power2.inOut",
			});
		};

		const morphToRect = () => {
			gsap.to(`#${rectId}`, {
				duration: 0.4,
				morphSVG: createRoundedRectPath(),
				ease: "power2.inOut",
				onUpdate: function () {
					if (onMorphToRect) onMorphToRect(element, this);
				},
			});
		};

		return { morphToBlob, morphToRect };
	};

	// Main initialization
	const elements = document.querySelectorAll(selector);

	elements.forEach((element, index) => {
		const targetElement = targetSelector
			? element.querySelector(targetSelector)
			: element;

		if (!targetElement) return;

		const svgElements = createSVGStructure(element, index);
		const { blobId, rectId } = svgElements;

		// Apply clip-path to target element
		targetElement.style.clipPath = `url(#${blobId})`;
		targetElement.style.webkitClipPath = `url(#${blobId})`;

		// Setup responsive behavior
		setupClipPath(element, svgElements, targetElement);
		setupResizeObserver(element, svgElements, targetElement);

		// Create and attach event handlers
		const { morphToBlob, morphToRect } = createMorphingHandlers(
			element,
			rectId
		);

		element.addEventListener("mouseenter", morphToBlob);
		element.addEventListener("mouseleave", morphToRect);
	});
};

const initMorphingCard = () => {
	createSVGMorphing({
		selector: ".c-item-card-morph",
		targetSelector: ".js-morph-thumbnail",
		blobSelector: "#svg-mask-blob path",
		defaultWidth: 225,
		defaultHeight: 307,
		defaultRadius: 8,
		onMorphToBlob: (element) => {
			if (element.classList.contains("has-border")) {
				element.style.setProperty("--border-color", "transparent");
			}
		},
		onMorphToRect: (element, tween) => {
			if (tween.progress() > 0.7 && element.classList.contains("has-border")) {
				element.style.setProperty("--border-color", "var(--cr-gray)");
			}
		},
	});
};

const initMorphingButton = () => {
	createSVGMorphing({
		selector: ".js-morph-button",
		targetSelector: null, // morph the button itself
		blobSelector: "#svg-mask-blob-button path",
		defaultWidth: 191,
		defaultHeight: 61,
		defaultRadius: 10,
	});
};

const initPageImageMask = () => {
	const pageImages = document.querySelectorAll(
		".article-content img, .p-general img"
	);

	if (pageImages.length === 0) return;

	const getAspectRatio = (img) => img.naturalHeight / img.naturalWidth;

	const getMaskType = (img) =>
		getAspectRatio(img) > 1 ? "rectangle-vertical" : "rectangle-horizontal";

	const applyMaskToImage = (img) => {
		if (img.closest("c-svg-mask")) return;

		const maskType = getMaskType(img);
		const maskElement = document.createElement("c-svg-mask");
		maskElement.className = `c-svg-mask c-svg-mask-${maskType}`;
		maskElement.setAttribute("data-mask", maskType);

		img.parentNode.insertBefore(maskElement, img);
		maskElement.appendChild(img);
	};

	const handleImage = (img) => {
		if (img.complete && img.naturalWidth > 0) {
			applyMaskToImage(img);
		} else {
			const handler = () => applyMaskToImage(img);
			img.addEventListener("load", handler, { once: true });
			img.addEventListener("error", handler, { once: true });
		}
	};

	pageImages.forEach(handleImage);
};

class ComponentSVGMask extends HTMLElement {
	constructor() {
		super();
		this.maskType = null;
		this.uniqueId = null;
		this.clippath = null;
		this.svg = null;
		this.resizeObserver = null;
		this.handleResize = this.handleResize.bind(this);
		this.isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
	}

	connectedCallback() {
		this.maskType = this.getAttribute("data-mask");

		this.uniqueId = `svg-mask-${this.maskType}-${Date.now()}-${Math.random()
			.toString(36)
			.slice(2, 9)}`;

		// Clone the original clipPath and give it a unique ID
		this.cloneAndSetupMask();

		this.clippath = document.querySelector(`#${this.uniqueId}`);
		this.svg = document.querySelector(".svg-mask-definitions");

		if (this.maskType && this.clippath && this.svg) {
			// Apply the unique clip-path to this element
			this.style.clipPath = `url(#${this.uniqueId})`;
			this.style.webkitClipPath = `url(#${this.uniqueId})`;

			this.initSVGScaling();

			if (this.isFirefox) {
				if (window.ResizeObserver) {
					this.resizeObserver = new ResizeObserver(() => {
						this.handleResize();
					});
					this.resizeObserver.observe(this);
				} else {
					window.addEventListener("resize", this.handleResize);
				}
			}
		}
	}

	cloneAndSetupMask() {
		const originalClipPath = document.querySelector(
			`#svg-mask-${this.maskType}`
		);
		if (!originalClipPath) return;

		const clonedClipPath = originalClipPath.cloneNode(true);
		clonedClipPath.id = this.uniqueId;

		const defs = document.querySelector(".svg-mask-definitions defs");
		if (defs) {
			defs.appendChild(clonedClipPath);
		}
	}

	disconnectedCallback() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}

		const uniqueClipPath = document.querySelector(`#${this.uniqueId}`);
		if (uniqueClipPath) {
			uniqueClipPath.remove();
		}
	}

	handleResize() {
		if (this.isFirefox) {
			const { width, height } = this.getMaskDimensions(this.maskType);
			if (width && height) {
				this.applyFirefoxWorkaround(width, height);
			}
		}
	}

	getMaskDimensions(maskType) {
		// Define original dimensions for each mask type
		const maskDimensions = {
			square: { width: 194.8, height: 210.9 },
			"square-sharp": { width: 180.1, height: 180.1 },
			"rectangle-horizontal": { width: 483.55, height: 278.65 },
			"rectangle-vertical": { width: 278.65, height: 483.55 },
			blob: { width: 225, height: 306.8 },
			frame: { width: 216.27, height: 216.27 },
		};

		return maskDimensions[maskType] || { width: 200, height: 200 };
	}

	initSVGScaling() {
		/*
		reference 1: https://stackoverflow.com/a/62834720
		reference 2: https://davesmyth.com/clip-path-scaling
		reference 3: https://css-tricks.com/scaling-svg-clipping-paths-css-use/

		Scale paths to fit objectBoundingBox (values between 0 and 1)
	*/
		const { width, height } = this.getMaskDimensions(this.maskType);

		if (this.isFirefox) {
			this.applyFirefoxWorkaround(width, height);
		} else {
			this.applyStandardScaling(width, height);
		}
	}

	applyStandardScaling(width, height) {
		const scaleX = 1 / width;
		const scaleY = 1 / height;

		// Ensure clipPath uses objectBoundingBox
		this.clippath.setAttribute("clipPathUnits", "objectBoundingBox");

		this.clippath.querySelectorAll("path").forEach((path) => {
			if (isFinite(scaleX) && isFinite(scaleY)) {
				path.setAttribute("transform", `scale(${scaleX}, ${scaleY})`);
			}
		});
	}

	applyFirefoxWorkaround(originalWidth, originalHeight) {
		// Change to userSpaceOnUse for Firefox
		this.clippath.setAttribute("clipPathUnits", "userSpaceOnUse");

		const containerRect = this.getBoundingClientRect();
		if (containerRect.width === 0 || containerRect.height === 0) {
			// Retry after a short delay if container is not rendered yet
			setTimeout(
				() => this.applyFirefoxWorkaround(originalWidth, originalHeight),
				10
			);
			return;
		}

		const scaleX = containerRect.width / originalWidth;
		const scaleY = containerRect.height / originalHeight;

		this.clippath.querySelectorAll("path").forEach((path) => {
			if (isFinite(scaleX) && isFinite(scaleY)) {
				path.setAttribute("transform", `scale(${scaleX}, ${scaleY})`);
			}
		});
	}
}
customElements.define("c-svg-mask", ComponentSVGMask);

class ComponentMarquee extends HTMLElement {
	constructor() {
		super();
		this.contentBlocks = Array.from(
			this.querySelectorAll(".js-marquee-content")
		);
		this.blockCount = this.contentBlocks[0]?.childNodes.length || 0;
		this.pixelPerSecond = 50;
		this.observer = null;
	}

	connectedCallback() {
		if (this.contentBlocks.length === 0) return;

		this.setupMarquee();
		this.setupResizeObserver();
	}

	disconnectedCallback() {
		this.observer?.disconnect();
	}

	setupMarquee() {
		const { clientWidth: blockWidth } = this.contentBlocks[0];
		const factor = Math.ceil(window.innerWidth / blockWidth);
		const speedFactor = parseFloat(this.dataset.speedFactor) || 1;
		const speed = (blockWidth * factor) / this.pixelPerSecond / speedFactor;

		this.contentBlocks.forEach((block) => {
			block.style.animationDuration = `${speed}s`;
			this.fillBlock(block, factor);
		});
	}

	fillBlock(block, factor) {
		if (!Number.isFinite(factor)) return;

		const fragment = document.createDocumentFragment();
		for (let i = 0; i < factor - 1; i++) {
			fragment.append(
				...Array.from(block.childNodes).map((node) => node.cloneNode(true))
			);
		}
		block.append(fragment);
	}

	setupResizeObserver() {
		this.observer = new ResizeObserver(this.handleResize.bind(this));
		this.observer.observe(this);
	}

	handleResize() {
		this.contentBlocks.forEach((block) => {
			block.style.animationDuration = "";
			while (block.childElementCount > this.blockCount) {
				block.lastElementChild.remove();
			}
		});
		this.setupMarquee();
	}
}
customElements.define("c-marquee", ComponentMarquee);

class ComponentSlider extends HTMLElement {
	constructor() {
		super();
		this.viewportNode = this.querySelector(".js-embla-viewport");
		this.prevButtonNode = this.querySelector(".js-embla-button-prev");
		this.nextButtonNode = this.querySelector(".js-embla-button-next");
		this.dotsNode = this.querySelector(".js-embla-dots");
		this.isVertical = this.dataset.emblaVertical === "true";

		// Plugins
		this.enableFade = this.dataset.emblaFade === "true";
		this.enableAutoHeight = this.dataset.emblaAutoHeight === "true";
		this.enableAutoplay = this.dataset.emblaAutoplay === "true";
		const autoplayDelay = this.hasAttribute("data-embla-delay")
			? parseInt(this.dataset.emblaDelay)
			: 4000;
		const autoplay = EmblaCarouselAutoplay({
			playOnInit: this.enableAutoplay,
			delay: autoplayDelay,
			stopOnInteraction: false,
			stopOnMouseEnter: true,
		});
		this.enableAutoScroll = this.dataset.emblaAutoScroll === "true";
		const autoScrollSpeed = this.hasAttribute("data-embla-scroll-speed")
			? parseInt(this.dataset.emblaScrollSpeed)
			: 2;
		const autoScrollDelay = this.hasAttribute("data-embla-scroll-delay")
			? parseInt(this.dataset.emblaScrollDelay)
			: 0;
		const autoScrollDirection =
			this.dataset.emblaScrollBackward === "true" ? "backward" : "forward";
		const autoScroll = EmblaCarouselAutoScroll({
			playOnInit: this.enableAutoScroll,
			speed: autoScrollSpeed,
			startDelay: autoScrollDelay,
			direction: autoScrollDirection,
			stopOnInteraction: false,
			stopOnMouseEnter: true,
		});

		const plugins = [
			EmblaCarouselClassNames(),
			...(vs.isTouchDevice ? [EmblaCarouselWheelGestures()] : []),
			...(this.enableAutoplay ? [autoplay] : []),
			...(this.enableAutoScroll ? [autoScroll] : []),
			...(this.enableFade ? [EmblaCarouselFade()] : []),
			...(this.enableAutoHeight ? [EmblaCarouselAutoHeight()] : []),
		];

		const options = {
			axis: this.isVertical ? "y" : "x",
			...JSON.parse(this.dataset.emblaSlider.replace(/'/g, '"').trim()),
		};

		// Init Embla
		this.embla = EmblaCarousel(this.viewportNode, options, plugins);
		// destroy embla if contains only one item
		this.slideNodes = this.embla.slideNodes();
		if (this.slideNodes.length === 1) {
			this.classList.add("is-inactive");
			this.embla.destroy();
		}
	}

	connectedCallback() {
		this.initializeNavigation();
		this.initializeColors();
	}

	// Prev & Next Buttons
	addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
		const togglePrevNextBtnsState = () => {
			if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled");
			else prevBtn.setAttribute("disabled", "disabled");

			if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled");
			else nextBtn.setAttribute("disabled", "disabled");
		};

		emblaApi
			.on("select", togglePrevNextBtnsState)
			.on("init", togglePrevNextBtnsState)
			.on("reInit", togglePrevNextBtnsState);

		return () => {
			prevBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("disabled");
		};
	};
	addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
		const scrollPrev = () => {
			emblaApi.scrollPrev();
		};
		const scrollNext = () => {
			emblaApi.scrollNext();
		};
		prevBtn.addEventListener("click", scrollPrev, false);
		nextBtn.addEventListener("click", scrollNext, false);

		const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
			emblaApi,
			prevBtn,
			nextBtn
		);

		return () => {
			removeTogglePrevNextBtnsActive();
			prevBtn.removeEventListener("click", scrollPrev, false);
			nextBtn.removeEventListener("click", scrollNext, false);
		};
	};

	// Dots
	addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
		let dotNodes = [];

		const addDotBtnsWithClickHandlers = () => {
			dotsNode.innerHTML = emblaApi
				.scrollSnapList()
				.map(
					() =>
						'<button class="embla__dot c-slider__dot increase-target-size" type="button" aria-label="Slider nav"></button>'
				)
				.join("");

			dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));

			dotNodes.forEach((dotNode, index) => {
				dotNode.addEventListener("click", () => {
					this.scrollToNodes(emblaApi, index);
				});
			});
		};

		const toggleDotBtnsActive = () => {
			const previous = emblaApi.previousScrollSnap();
			const selected = emblaApi.selectedScrollSnap();
			dotNodes[previous].classList.remove("c-slider__dot--selected");
			dotNodes[selected].classList.add("c-slider__dot--selected");
		};

		emblaApi
			.on("init", addDotBtnsWithClickHandlers)
			.on("reInit", addDotBtnsWithClickHandlers)
			.on("init", toggleDotBtnsActive)
			.on("reInit", toggleDotBtnsActive)
			.on("select", toggleDotBtnsActive);

		return () => {
			dotsNode.innerHTML = "";
		};
	};

	initializeNavigation = () => {
		if (this.slideNodes.length === 1) return;
		if (this.prevButtonNode && this.nextButtonNode) {
			const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
				this.embla,
				this.prevButtonNode,
				this.nextButtonNode
			);
			this.embla.on("destroy", removePrevNextBtnsClickHandlers);
		}
		if (this.dotsNode) {
			const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(
				this.embla,
				this.dotsNode
			);
			this.embla.on("destroy", removeDotBtnsAndClickHandlers);
		}
	};

	// Color sync: active slide → viewport CSS vars
	initializeColors = () => {
		if (!this.embla || this.slideNodes.length === 0) return;
		if (!this.querySelector("[data-bg-color]")) return;

		const viewportNode = this.embla.rootNode();
		const applyColors = () => {
			const index = this.embla.selectedScrollSnap();
			const slide = this.slideNodes[index];

			const bgColor = slide.dataset.bgColor;
			const titleColor = slide.dataset.titleColor;
			const bodyTextColor = slide.dataset.bodyTextColor;

			viewportNode.style.setProperty("--slide-bg", bgColor);
			viewportNode.style.setProperty("--slide-title-color", titleColor);
			viewportNode.style.setProperty("--slide-body-text-color", bodyTextColor);
		};

		applyColors();

		this.embla
			.on("select", applyColors)
			.on("reInit", applyColors)
			.on("init", applyColors);
	};

	scrollToNodes = (emblaApi, index) => {
		emblaApi.scrollTo(index);
	};
}
customElements.define("c-slider", ComponentSlider);

class ComponentLogoAnimation extends HTMLElement {
	constructor() {
		super();
		this.textOptions = [];
		this.emojiOptions = [];
		this.combinedOptions = [];
		this.lastTextTime = 0;
		this.textInterval = 300;
		this.isHovering = false;
		this.boundHandlers = {};
		this.lastMousePosition = { x: 0, y: 0 };
		this.currentMousePosition = { x: 0, y: 0 };
		this.emojiTemplates = {};
	}

	connectedCallback() {
		this.init();
		this.handleResponsiveDisplay();
	}

	disconnectedCallback() {
		this.cleanup();
	}

	init() {
		this.loadEmojiTemplates();
		this.setupEmojiOptions();
		this.setupTextOptions();
		this.combineCombinedOptions();
		this.bindEvents();
	}

	loadEmojiTemplates() {
		const emojiScript = document.getElementById("emoji-templates");
		if (emojiScript) {
			try {
				this.emojiTemplates = JSON.parse(emojiScript.textContent);
			} catch (error) {
				console.error("Failed to parse emoji templates:", error);
				this.emojiTemplates = {};
			}
		}
	}

	setupEmojiOptions() {
		const availableEmojis = Object.keys(this.emojiTemplates);

		this.emojiOptions = availableEmojis.map((name) => ({
			type: "emoji",
			name: name,
			svg: this.emojiTemplates[name],
		}));
	}

	handleResponsiveDisplay() {
		const mediaQuery = window.matchMedia("(max-width: 1024px)");

		if (mediaQuery.matches) {
			this.createStaticStickers();
		} else {
			this.removeStaticStickers();
		}

		// Listen for screen size changes
		mediaQuery.addEventListener("change", (e) => {
			if (e.matches) {
				this.createStaticStickers();
			} else {
				this.removeStaticStickers();
			}
		});
	}

	createStaticStickers() {
		this.removeStaticStickers();
		if (this.combinedOptions.length === 0) return;

		const positions = [
			{ x: 45, y: 25, rotation: -8 }, // Top left
			{ x: 95, y: 75, rotation: -5 }, // Bottom right
			{ x: 65, y: 50, rotation: -15 }, // Middle center
			{ x: 30, y: 80, rotation: 10 }, // Bottom left
			{ x: 90, y: 20, rotation: 8 }, // Top right
		];

		positions.forEach((pos, index) => {
			const element = document.createElement("div");
			const itemIndex = index % this.combinedOptions.length;
			const item = this.combinedOptions[itemIndex];
			element.textContent = item.text;
			element.className = "c-logo-floating-text c-logo-static-text";
			element.setAttribute("data-text", item.text);

			gsap.set(element, {
				left: pos.x + "%",
				top: pos.y + "%",
				rotation: pos.rotation,
				xPercent: -50,
				yPercent: -50,
			});

			this.appendChild(element);
		});
	}

	removeStaticStickers() {
		const staticStickers = this.querySelectorAll(".c-logo-static-text");
		staticStickers.forEach((sticker) => {
			this.removeChild(sticker);
		});
	}

	setupTextOptions() {
		const textData = this.dataset.textOptions;
		if (textData) {
			try {
				this.textOptions = JSON.parse(textData).map((text) => ({
					type: "text",
					text,
				}));
			} catch (error) {
				console.error("Invalid text options data:", error);
				this.textOptions = [];
			}
		}
	}

	combineCombinedOptions() {
		this.combinedOptions = [...this.textOptions, ...this.emojiOptions];
	}

	bindEvents() {
		this.boundHandlers = {
			mouseenter: () => (this.isHovering = true),
			mouseleave: () => (this.isHovering = false),
			mousemove: this.handleMouseMove.bind(this),
		};

		Object.entries(this.boundHandlers).forEach(([event, handler]) => {
			this.addEventListener(event, handler);
		});
	}

	handleMouseMove(e) {
		if (!window.matchMedia("(max-width: 1024px)").matches && this.isHovering) {
			const rect = this.getBoundingClientRect();

			// Update mouse positions
			this.lastMousePosition = { ...this.currentMousePosition };
			this.currentMousePosition = {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			};

			this.createFloatingText(
				this.currentMousePosition.x,
				this.currentMousePosition.y
			);
		}
	}

	createFloatingText(x, y) {
		const currentTime = Date.now();
		if (
			currentTime - this.lastTextTime < this.textInterval ||
			this.combinedOptions.length === 0
		) {
			return;
		}

		const element = document.createElement("div");
		const randomItem =
			this.combinedOptions[
				Math.floor(Math.random() * this.combinedOptions.length)
			];

		if (randomItem.type === "emoji") {
			element.innerHTML = randomItem.svg;
			element.className = "c-logo-floating-text c-logo-emoji";
		} else {
			element.textContent = randomItem.text;
			element.className = "c-logo-floating-text";
			element.setAttribute("data-text", randomItem.text);
		}

		// Calculate movement direction (opposite to mouse movement)
		const deltaX = this.currentMousePosition.x - this.lastMousePosition.x;
		const deltaY = this.currentMousePosition.y - this.lastMousePosition.y;

		// Generate random rotation between -15 and 15 degrees
		const randomRotation = (Math.random() - 0.5) * 30; // -15 to 15 degrees

		gsap.set(element, {
			left: x - 20,
			top: y - 20,
			scale: 0,
			rotation: randomRotation,
			opacity: 0,
		});

		this.appendChild(element);

		const tl = gsap.timeline();

		tl.to(element, {
			x: deltaX,
			y: deltaY,
			duration: 0.1,
		})
			.to(
				element,
				{
					scale: 1,
					opacity: 1,
					duration: 0.2,
					ease: "back.out(1.7)",
				},
				"-=0.2"
			)
			.to(element, {
				duration: 0.3,
			})
			.to(element, {
				y: "+=100vh",
				ease: "power2.in",
				duration: 0.5,
				onComplete: () => {
					setTimeout(() => {
						if (element.parentNode === this) {
							this.removeChild(element);
						}
					}, 500);
				},
			});

		this.lastTextTime = currentTime;
	}

	cleanup() {
		this.removeStaticStickers();

		gsap.killTweensOf(this.querySelectorAll(".c-logo-floating-text"));

		Object.entries(this.boundHandlers).forEach(([event, handler]) => {
			this.removeEventListener(event, handler);
		});
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === "data-text-options" && oldValue !== newValue) {
			this.setupTextOptions();
			this.combineCombinedOptions();
		}
	}
}
customElements.define("c-logo-animation", ComponentLogoAnimation);

// Load Vimeo / Youtube players
let allPlayersObj = {};
// youtube fix - lace this event at global level
window.onYouTubeIframeAPIReady = () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="youtube"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new YT.Player(video, {
				videoId: externalVideoId,
				playerVars: { playsinline: 1 },
			});
			allPlayersObj[externalVideoId] = player;
		});
};

window.addEventListener("load", () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="vimeo"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new Vimeo.Player(video);
			player.on("loaded", () => (allPlayersObj[externalVideoId] = player));
		});

	setTimeout(() => {
		// set a slight delay for player to load completely
		initVideo();
	}, 400);
});

document.addEventListener("DOMContentLoaded", () => {
	// Run common initializations
	[
		initGSAP,
		initHeader,
		initHeadlineAnimation,
		initSectionAnimation,
		initButtonAnimation,
		initPageImageMask,
		initVideo,
	].forEach(safeRun);

	// Initialize specific features
	if (document.querySelector(".c-item-card-morph")) safeRun(initMorphingCard);
	if (document.querySelector(".js-morph-button")) safeRun(initMorphingButton);
	if (document.querySelector(".c-quiz-modal")) safeRun(initQuiz);
});

// Safely run a function and catch errors
function safeRun(fn) {
	try {
		fn?.();
	} catch (error) {
		console.error(`Init error in ${fn.name}:`, error);
	}
}

ariaFocusManager.init();
